import { VERSION as deckGlVersion } from '@deck.gl/core'

import { MVTWorkerLoader } from '@loaders.gl/mvt'

// all .version properties in loaders.gl/.. workers are offset by one
//   https://github.com/visgl/loaders.gl/issues/3202
// mark them here until we can rely on those version
const mvtWorkerLoaderVersion = `quirk-${MVTWorkerLoader.version}`

export const deckWorkersDir = `workers/deck.gl@${deckGlVersion}`
export const mvtWorkerDir = `workers/loaders.gl/mvt@${mvtWorkerLoaderVersion}`

function getLocalDeckWorkerUrl(workerScriptName: string, baseDir: string) {
  return `/${baseDir}/${workerScriptName}-worker.js`
}

function getLocalDeckCartoWorkerLoaderOptions(workerScriptName: string, baseDir: string) {
  return {
    [workerScriptName]: {
      workerUrl: getLocalDeckWorkerUrl(workerScriptName, baseDir)
    }
  }
}

export const cartoVectorTileLoadOptions = {
  ...getLocalDeckCartoWorkerLoaderOptions('cartoVectorTile', deckWorkersDir),
  ...getLocalDeckCartoWorkerLoaderOptions('mvt', mvtWorkerDir)
}

export const cartoSpatialTileLoadOptions = getLocalDeckCartoWorkerLoaderOptions('cartoSpatialTile', deckWorkersDir)
export const cartoRasterTileLoadOptions = getLocalDeckCartoWorkerLoaderOptions('cartoRasterTile', deckWorkersDir)

export async function fetchWorkerScriptAsBlobUrl(url: string | URL): Promise<string> {
  const response = await fetch(url)
  const blob = await response.blob()
  return URL.createObjectURL(blob)
}

interface WidgetWorkerOptionsResolverDeps {
  isDev: boolean
  isInSandboxedIframe: () => boolean
  workerUrl: string
  resolveUrl: (url: string) => URL
  fetchWorkerScriptAsBlobUrl: (url: URL) => Promise<string>
}

// Dev first: the dev worker is an unbundled vite module worker whose imports break
// if blob-wrapped. Sandboxed iframes need the blob wrap — their opaque origin
// cannot load worker scripts from the app origin directly.
export function createWidgetWorkerOptionsResolver(deps: WidgetWorkerOptionsResolverDeps) {
  let blobUrl: string | undefined
  return async function getWidgetWorkerOptions() {
    if (deps.isDev) {
      return { widgetWorkerUrl: deps.workerUrl, widgetWorkerType: 'module' as const }
    }
    if (deps.isInSandboxedIframe()) {
      if (!blobUrl) {
        blobUrl = await deps.fetchWorkerScriptAsBlobUrl(deps.resolveUrl(deps.workerUrl))
      }
      return { widgetWorkerUrl: blobUrl }
    }
    return { widgetWorkerUrl: deps.resolveUrl(deps.workerUrl).href }
  }
}

export async function preloadWorkerScriptsAsBlobs() {
  async function fetchWorkerScriptAsBlobUrl(url: string | URL): Promise<string> {
    const response = await fetch(url)
    const blob = await response.blob()
    return URL.createObjectURL(blob)
  }

  await Promise.all([
    fetchWorkerScriptAsBlobUrl(cartoVectorTileLoadOptions.cartoVectorTile.workerUrl).then((url) => {
      cartoVectorTileLoadOptions.cartoVectorTile.workerUrl = url
    }),
    fetchWorkerScriptAsBlobUrl(cartoVectorTileLoadOptions.mvt.workerUrl).then((url) => {
      cartoVectorTileLoadOptions.mvt.workerUrl = url
    }),
    fetchWorkerScriptAsBlobUrl(cartoSpatialTileLoadOptions.cartoSpatialTile.workerUrl).then((url) => {
      cartoSpatialTileLoadOptions.cartoSpatialTile.workerUrl = url
    }),
    fetchWorkerScriptAsBlobUrl(cartoRasterTileLoadOptions.cartoRasterTile.workerUrl).then((url) => {
      cartoRasterTileLoadOptions.cartoRasterTile.workerUrl = url
    })
  ])
}
